/**
 * Hook for managing overlay SkinnedMesh nodes for a parent SkinnedMesh.
 *
 * Depth prepass, wireframe overlay, and selection outline all follow the
 * same pattern: clone the parent's geometry + skeleton into a child
 * SkinnedMesh with a specific material, renderOrder, and tag flag.
 * This hook encapsulates that pattern to eliminate duplication.
 */

import { useEffect } from "react";
import * as THREE from "three/webgpu";
import { noRaycast } from "./sharedMaterials.ts";

/**
 * Attach (or remove) an overlay SkinnedMesh to `parent`.
 *
 * When `active` is true, creates a SkinnedMesh clone sharing the parent's
 * geometry, skeleton, and morph targets, tagged with `_${flag}` for lookup.
 * Child overlays inherit the base mesh visibility; sibling overlays are for
 * states like hidden-selection ghosts where the base mesh itself must be
 * invisible but a skinned proxy still has to render.
 * When `active` becomes false (or on unmount), the overlay is removed.
 *
 * @param parent      The SkinnedMesh to attach the overlay to.
 * @param active      Whether the overlay should be present.
 * @param material    Material for the overlay mesh.
 * @param renderOrder Draw order relative to the parent.
 * @param flag        Unique tag string (e.g. "depthPrepass", "wireOverlay").
 * @param deps        Additional dependency values that should trigger re-creation.
 * @param options     Optional config. Overlays are excluded from normal export
 *                    renders by default; layered passes can opt out.
 */
export function useSkinnedOverlay(
  parent: THREE.SkinnedMesh | null,
  active: boolean,
  material: THREE.Material,
  renderOrder: number,
  flag: string,
  deps: readonly unknown[] = [],
  options?: {
    excludeFromExport?: boolean;
    layer?: number;
    attach?: "child" | "sibling";
  },
): void {
  useEffect(() => {
    if (!parent) return;
    const attach = options?.attach ?? "child";
    const container = attach === "sibling" ? parent.parent : parent;
    if (!container) return;

    // Remove any existing overlay with this flag
    const existing = container.children.find((c) => (c as any)[`_${flag}`]);
    if (existing) container.remove(existing);

    if (active) {
      const clone = new THREE.SkinnedMesh(parent.geometry, material);
      clone.bind(parent.skeleton, parent.bindMatrix);
      if (attach === "sibling") {
        clone.position.copy(parent.position);
        clone.quaternion.copy(parent.quaternion);
        clone.scale.copy(parent.scale);
      }
      if (parent.morphTargetDictionary) {
        clone.morphTargetDictionary = parent.morphTargetDictionary;
      }
      if (parent.morphTargetInfluences) {
        clone.morphTargetInfluences = parent.morphTargetInfluences;
      }
      clone.renderOrder = renderOrder;
      clone.raycast = noRaycast;
      if (options?.layer != null) {
        clone.layers.set(options.layer);
      }
      (clone as any)[`_${flag}`] = true;
      if (parent.userData?.poserefInkingInclude) {
        clone.userData.poserefInkingInclude =
          parent.userData.poserefInkingInclude;
      }
      if (options?.excludeFromExport ?? true) {
        clone.userData.excludeFromExport = true;
      }
      container.add(clone);
    }

    return () => {
      const overlay = container.children.find((c) => (c as any)[`_${flag}`]);
      if (overlay) container.remove(overlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    parent,
    active,
    material,
    renderOrder,
    flag,
    options?.attach,
    options?.excludeFromExport,
    options?.layer,
    ...deps,
  ]);
}
